import { Issue, JobData, Nullable, RawIssue, Rule, present } from './types'
import { message } from './messages'
import { normaliseDBSText, validateDBSText } from './text'

/**
 * The job-role rules: what the DBS receives as PositionAppliedFor and the RBdetails flags (DisclosureType,
 * WorkingWithChildren, WorkingWithVulnerableAdults, WorkingAtHomeAddress, Volunteer).
 */

/**
 * The PositionAppliedFor field declares the workforce the applicant is applying to work with and the role they are
 * applying for, in the format the DBS asked for (DBS-63): `[Workforce] WF [Role]`, upper-cased.
 *
 * It applies to Standard applications as well as Enhanced (DBS-331). The DBS eligibility guidance of 1 September
 * 2026 says the workforce entered in Position Applied For should be appropriate to the role whatever the level of
 * check; from March 2025 to October 2026 a Standard role was sent bare.
 */
export const computePositionAppliedFor = (job: { role: string, application_workforce?: Nullable<string> }): string => {
  switch (job.application_workforce) {
    case 'adult_only':
      return `Adult WF ${job.role}`.toUpperCase()
    case 'child_only':
      return `Child WF ${job.role}`.toUpperCase()
    case 'child_and_adult':
      return `Child and Adult WF ${job.role}`.toUpperCase()
    default:
      return `Other WF ${job.role}`.toUpperCase()
  }
}

/**
 * @param positionAppliedFor The *computed* value (workforce prefix included, upper-cased), since that is what the
 * DBS receives and what the 60 character limit applies to.
 * @returns true if valid, otherwise the reason it was rejected.
 */
export const validatePositionAppliedFor = (positionAppliedFor: string): string | true =>
  validateDBSText(positionAppliedFor)

const enhancedRequired = (id: 'job.enhancedRequired.children' | 'job.enhancedRequired.adults' | 'job.enhancedRequired.home', field: keyof JobData, title: string, dbsCodes: number[]): Rule<JobData> => ({
  id, title, dbsCodes, spec: ['4#36'], severity: 'error', pages: [], stage: 'page',
  check: job => job[field] === true && job.application_type !== 'enhanced'
    ? [{ path: ['application_type'], message: message(id) }, { path: [field], message: message(id) }]
    : [],
})

export const jobRules: Rule<JobData>[] = [
  {
    id: 'job.position', title: 'PositionAppliedFor fits the DBS character set and length', dbsCodes: [102, 110], spec: ['XSD'],
    severity: 'error', pages: [], stage: 'page',
    check: job => {
      if (!present(job.role)) return []
      // Validate what will actually be stored and sent, not what was typed
      const position = computePositionAppliedFor({ role: normaliseDBSText(job.role).trim(), application_workforce: job.application_workforce })
      const result = validatePositionAppliedFor(position)
      return result === true ? [] : [{ path: ['role'], message: message('job.position', result) }]
    },
  },
  enhancedRequired('job.enhancedRequired.children', 'working_with_children', 'A children\'s barred-list check needs an Enhanced application', [123]),
  enhancedRequired('job.enhancedRequired.adults', 'working_with_adults', 'An adults\' barred-list check needs an Enhanced application', [123]),
  enhancedRequired('job.enhancedRequired.home', 'working_at_home_address', 'Working at the applicant\'s home needs an Enhanced application', [124]),
]

export const evaluateJobRules = (job: JobData): Issue[] => {
  const issues: Issue[] = []
  for (let r = 0; r < jobRules.length; r++) {
    const rule = jobRules[r]
    const raws: RawIssue[] = rule.check(job, { at: 'server', today: '', complete: false })
    for (let i = 0; i < raws.length; i++) {
      issues.push({ ruleId: rule.id, path: raws[i].path, message: raws[i].message, severity: rule.severity, dbsCodes: rule.dbsCodes })
    }
  }
  return issues
}
